import * as types from 'inventory-listing/actions/listingUsedActionTypes.js';

import { WindowProxy } from '@sm360/widget-commons-core-services';
import { PurchaseMethods, Vehicle, ShowroomService } from '@sm360/widget-commons-vehicle';
import { LocalStorageService, StateService } from '@sm360/widget-commons-general';

import * as ListingUsedService from 'inventory-listing/services/ListingUsedService/ListingUsedService.js';
import UrlManagement from 'inventory-listing/services/UrlManagement/UrlManagement.js';

const { PurchaseMethodsActions } = PurchaseMethods;
const { VehicleService } = Vehicle;

function addVehicles(vehicles) {
    return { type: types.ADD_VEHICLES, vehicles };
}

export function setVehicles(vehicles) {
    return { type: types.SET_VEHICLES, vehicles };
}

export function addLoadMoreVehicles(vehicles) {
    return { type: types.LOAD_VEHICLES, vehicles };
}

export function loadMoreVehicles(vehicles) {
    return (dispatch) => {
        return dispatch(addLoadMoreVehicles(vehicles));
    };
}

export function loadAndSetVehicles(vehicles) {
    return (dispatch) => {
        return dispatch(addVehicles(vehicles));
    };
}

function setPurchaseMethod(purchaseMethod) {
    return { type: types.SELECT_PURCHASE_METHOD, purchaseMethod };
}

function setMaximumTerm(maximumTerm) {
    return { type: types.SELECT_MAXIMUM_TERM, maximumTerm };
}

export function setListingUsed(listingUsed) {
    // Update the URL with the new listingUsed
    try {
        UrlManagement.changeUrl(listingUsed);
    } catch (error) {
        console.error(error);
    }
    return { type: types.SET_LISTING_USED, listingUsed };
}

export function setContextUrl(contextUrl) {
    return { type: types.SET_CONTEXT_URL, contextUrl };
}

export function setVdpUrl(vdpUrl) {
    return { type: types.SET_VDP_URL, vdpUrl };
}

export function setVehicleCriteria(vehicleCriteria) {
    return { type: types.SET_VEHICLE_CRITERIA, vehicleCriteria };
}

export function selectPurchaseMethod(purchaseMethod) {
    return (dispatch) => {
        dispatch(setPurchaseMethod(purchaseMethod));
    };
}

// Unlikely useful after V2.5 API migration
export function loadVehiclesPayments(vehicles, purchaseMethod, termMinimum, termMaximum) {
    if (!vehicles || !vehicles.length) {
        return () => {};
    }

    return (dispatch, getState) => {
        return ShowroomService.getVehiclesPayments(vehicles, purchaseMethod, termMinimum, termMaximum)
            .then((vehiclesPayments) => {
                const state = getState();
                const statePaymentOptions = state.listingUsed.paymentOptions;
                const selectedPurchaseMethod = statePaymentOptions.purchaseMethod;
                const windowProxy = WindowProxy.get();
                let tradeinValue = 0;
                let lienValue = 0;
                if (windowProxy && windowProxy.localStorage) {
                    tradeinValue = parseInt(windowProxy.localStorage.tradeIn, 10);
                    lienValue = parseInt(windowProxy.localStorage.lien, 10);
                }
                const purchaseMethods = vehiclesPayments.length > 0 ? vehiclesPayments[0].purchaseMethods : null;
                const updatedVehiclesPayments = vehiclesPayments.map((vehicle) => {
                    const newVehicle = VehicleService.setBestPaymentTerm(
                        VehicleService.selectPaymentFrequency(
                            VehicleService.setLien(VehicleService.setTradeIn(vehicle, tradeinValue), lienValue),
                            statePaymentOptions.preferredPaymentFrequency
                        ),
                        statePaymentOptions.maximumTerm
                    );
                    if (statePaymentOptions.initialTargetPayment > 0) {
                        return VehicleService.setTargetPayment(newVehicle, statePaymentOptions.initialTargetPayment);
                    }
                    return VehicleService.setCashDown(newVehicle, statePaymentOptions.initialCashDown);
                });
                dispatch(addVehicles(updatedVehiclesPayments));
                dispatch(PurchaseMethodsActions.loadPurchaseMethodsSuccess(purchaseMethods));
                if (selectedPurchaseMethod !== purchaseMethod) {
                    dispatch(selectPurchaseMethod(selectedPurchaseMethod));
                }
            })
            .catch((error) => {
                throw error;
            });
    };
}

export function selectMaximumTerm(maximumTerm) {
    return (dispatch, getState) => {
        const state = getState();
        const url = UrlManagement.buildUrl(state.listingUsed);

        StateService.addParamToCurrentState(
            url,
            'listing-used',
            {
                filter: 'filter',
            },
            'maximumTerm',
            maximumTerm
        );

        return dispatch(setMaximumTerm(maximumTerm));
    };
}

export function selectPreferredPaymentFrequency(paymentFrequency) {
    return (dispatch, getState) => {
        const state = getState();

        const listingUsed = ListingUsedService.setPreferredPaymentFrequency(state.listingUsed, paymentFrequency);
        const url = UrlManagement.buildUrl(listingUsed);

        StateService.addParamToCurrentState(
            url,
            'listing-used',
            {
                filter: 'filter',
            },
            'preferredPaymentFrequency',
            paymentFrequency
        );

        return dispatch(setListingUsed(listingUsed));
    };
}

export function setInitialCashDown(cashDown) {
    return { type: types.SET_INITIAL_CASH_DOWN, cashDown };
}

export function setInitialTargetPayment(targetPayment) {
    return { type: types.SET_INITIAL_TARGET_PAYMENT, targetPayment };
}

export function setCashDown(cashDown) {
    LocalStorageService.set('cashDown', cashDown);
    return (dispatch, getState) => {
        const state = getState();
        const url = UrlManagement.buildUrl(state.listingUsed);

        StateService.addParamToCurrentState(
            url,
            'listing-used',
            {
                filter: 'filter',
            },
            'initialCashDown',
            cashDown
        );

        dispatch(setInitialCashDown(cashDown));
    };
}

export function setTargetPayment(targetPayment) {
    LocalStorageService.set('targetPayment', targetPayment);
    return (dispatch, getState) => {
        const state = getState();
        const url = UrlManagement.buildUrl(state.listingUsed);

        StateService.addParamToCurrentState(
            url,
            'listing-used',
            {
                filter: 'filter',
            },
            'initialTargetPayment',
            targetPayment
        );

        dispatch(setInitialTargetPayment(targetPayment));
    };
}

export function setTradeIn(tradeIn) {
    LocalStorageService.set('tradeIn', tradeIn);
    return (dispatch) => {
        dispatch(PurchaseMethodsActions.setTradeIn(tradeIn));
    };
}

export function setLien(lien) {
    LocalStorageService.set('lien', lien);
    return (dispatch) => {
        dispatch(PurchaseMethodsActions.setLien(lien));
    };
}

export function setPaymentRange(paymentRange) {
    return { type: types.SET_PAYMENT_RANGE, paymentRange };
}

export function filterPayments(paymentRange) {
    return { type: types.SET_PAYMENT_RANGE, paymentRange };
}

export function translateListingUsed() {
    return (dispatch, getState) => {
        const state = getState();
        const listingUsed = ListingUsedService.translateListingUsed(state.listingUsed);

        dispatch(setListingUsed(listingUsed));
    };
}

export function updateVehicleCriteria(metadatas, attribute) {
    return (dispatch, getState) => {
        const state = getState();
        const listingUsed = ListingUsedService.addVehicleCriteria(state.listingUsed, { metadatas, attribute });

        try {
            UrlManagement.changeUrl(listingUsed);
        } catch (error) {
            console.error(error);
            return;
        }

        dispatch(setVehicleCriteria(listingUsed.vehicleCriteria));
    };
}

export function resetVehicleCriteria() {
    return (dispatch, getState) => {
        const state = getState();
        const listingUsed = ListingUsedService.resetVehicleCriteria(state.listingUsed);

        UrlManagement.changeUrl(listingUsed);

        dispatch(setVehicleCriteria(listingUsed.vehicleCriteria));
    };
}

/**
 * Update the payment Frequencies and terms returned by API for selected payment method
 * @param [paymentFrequencies] payment frequencies - [12, 26,52]
 * @param [term] payment terms - [24, 36, 48, 60, 72, 84]
 */
export function updateAvailablePaymentOptions(paymentFrequencies, terms, defaultTerm) {
    return (dispatch, getState) => {
        const state = getState();
        const listingUsed = ListingUsedService.updateAvailablePaymentOptions(
            state.listingUsed,
            paymentFrequencies,
            terms,
            defaultTerm
        );
        dispatch(setListingUsed(listingUsed));
    };
}

export function updateVehicleSorting(field, order) {
    const sorting = {
        vehicleSortParameter: field,
        direction: order,
    };

    return { type: types.SET_VEHICLE_SORT, sorting };
}

export function resetVehicleSortingToDefault() {
    return { type: types.SET_VEHICLE_SORT, sorting: {} };
}

export function setColanders(colanders) {
    return { type: types.SET_COLANDERS, colanders };
}

export function setMetadatas(metadatas) {
    return { type: types.SET_METADATAS, metadatas };
}

export function setGenericDisclaimers(disclaimers) {
    return (dispatch) => {
        dispatch({ type: types.SET_GENERIC_DISCLAIMERS, genericDisclaimers: disclaimers });
    };
}

function setPreferredPurchaseMethod(method) {
    return { type: types.SET_PREFERRED_PURCHASE_METHOD, preferredPurchaseMethod: method };
}

function replaceUrl(url) {
    return { type: types.REPLACE_URL, url };
}

export function selectPreferredPurchaseMethod(method) {
    return (dispatch, getState) => {
        dispatch(setPreferredPurchaseMethod(method));
        dispatch(selectPurchaseMethod(method));

        const state = getState();
        // TODO: #improve - add some REAL utilities to handle url, params and browser history
        const url = UrlManagement.buildUrl(state.listingUsed);
        StateService.addParamToCurrentState(url, 'listing', {}, 'preferredPurchaseMethod', method);
        // TODO: #improve - url is not returned because StateService doesn't make sense
        dispatch(replaceUrl(url));
    };
}
